// Rules tab — toolbar (templates / domain-flow / new-rule) + list of rule cards.
// Extracted from VoiceFabricPage as part of the iter-35 component decomp.
import React from 'react';
import {
  Zap, Plus, Trash2, Edit3, Loader2, Sparkles,
  BarChart3, Network,
} from 'lucide-react';

const RuleToolbar = ({ count, domainId, onOpenTemplates, onNewRule }) => (
  <div className="flex justify-between items-center">
    <p className="text-white/50 text-sm">
      {count} rule{count !== 1 ? 's' : ''} for this domain
    </p>
    <div className="flex gap-2">
      <button
        onClick={onOpenTemplates}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-sm font-medium rounded-lg"
        data-testid="vf-browse-templates-btn"
      >
        <Sparkles className="w-4 h-4" />
        Browse Templates
      </button>
      {domainId && (
        <a
          href={`/admin/voicefabric/flow/${domainId}`}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-sm font-medium rounded-lg"
          data-testid="vf-domain-flow-btn"
        >
          <Network className="w-4 h-4" />
          Domain Flow
        </a>
      )}
      <button
        onClick={onNewRule}
        className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg"
        data-testid="vf-new-rule-btn"
      >
        <Plus className="w-4 h-4" />
        New Rule
      </button>
    </div>
  </div>
);

const EmptyState = ({ onOpenTemplates, onNewRule }) => (
  <div className="bg-white/5 border border-dashed border-white/10 rounded-2xl p-12 text-center">
    <Zap className="w-10 h-10 mx-auto mb-3 text-white/20" />
    <p className="text-white/50 text-sm mb-1">No rules yet</p>
    <p className="text-white/30 text-xs mb-5">Start with a ready-made template or write your own.</p>
    <div className="flex justify-center gap-2">
      <button
        onClick={onOpenTemplates}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-sm font-medium rounded-lg"
        data-testid="vf-empty-browse-templates-btn"
      >
        <Sparkles className="w-4 h-4" /> Browse Templates
      </button>
      <button
        onClick={onNewRule}
        className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg"
      >
        <Plus className="w-4 h-4" /> New Rule
      </button>
    </div>
  </div>
);

const RuleCardActions = ({ rule, domainId, onMetrics, onToggle, onEdit, onDelete }) => (
  <div className="flex items-center gap-1 shrink-0">
    <button
      onClick={() => onMetrics(rule)}
      title="View performance metrics"
      className="p-2 text-white/40 hover:text-teal-400 transition-colors"
      data-testid={`vf-metrics-${rule.id}`}
    >
      <BarChart3 className="w-4 h-4" />
    </button>
    <a
      href={`/admin/voicefabric/flow/${domainId}/${rule.id}`}
      title="Open visual flow editor"
      className="p-2 text-white/40 hover:text-teal-400 transition-colors"
      data-testid={`vf-flow-${rule.id}`}
    >
      <Network className="w-4 h-4" />
    </a>
    <button
      onClick={() => onToggle(rule)}
      title={rule.enabled ? 'Disable' : 'Enable'}
      className={`w-10 h-5 rounded-full transition-colors relative ${rule.enabled ? 'bg-teal-500' : 'bg-white/10'}`}
      data-testid={`vf-toggle-${rule.id}`}
    >
      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${rule.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
    <button
      onClick={() => onEdit(rule)}
      className="p-2 text-white/40 hover:text-teal-400 transition-colors"
      data-testid={`vf-edit-${rule.id}`}
    >
      <Edit3 className="w-4 h-4" />
    </button>
    <button
      onClick={() => onDelete(rule)}
      className="p-2 text-white/40 hover:text-red-400 transition-colors"
      data-testid={`vf-delete-${rule.id}`}
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

const RuleCard = ({ rule, actionLabelFor, domainId, onMetrics, onToggle, onEdit, onDelete }) => {
  const cfg = actionLabelFor(rule.action_type);
  const Icon = cfg.icon;
  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-colors"
      data-testid={`vf-rule-card-${rule.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white font-medium text-sm truncate">{rule.name}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${cfg.color}`}>
              <Icon className="w-3 h-3" />
              {cfg.label}
            </span>
            {!rule.enabled && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/30">Disabled</span>
            )}
          </div>
          {rule.description && <p className="text-white/40 text-xs mb-2">{rule.description}</p>}
          <p className="text-white/50 text-xs font-mono line-clamp-2">{rule.trigger_prompt}</p>
        </div>
        <RuleCardActions
          rule={rule} domainId={domainId}
          onMetrics={onMetrics} onToggle={onToggle}
          onEdit={onEdit} onDelete={onDelete}
        />
      </div>
    </div>
  );
};

export const RulesTab = ({
  rules, loading, actionLabelFor, domainId,
  onOpenTemplates, onNewRule, onMetrics, onToggle, onEdit, onDelete,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-7 h-7 text-white/20 animate-spin" />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <RuleToolbar
        count={rules.length} domainId={domainId}
        onOpenTemplates={onOpenTemplates} onNewRule={onNewRule}
      />
      {rules.length === 0 ? (
        <EmptyState onOpenTemplates={onOpenTemplates} onNewRule={onNewRule} />
      ) : (
        <div className="space-y-2">
          {rules.map(r => (
            <RuleCard
              key={r.id}
              rule={r}
              actionLabelFor={actionLabelFor}
              domainId={domainId}
              onMetrics={onMetrics} onToggle={onToggle}
              onEdit={onEdit} onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};
